import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FileText, 
  Upload, 
  Download, 
  Trash2, 
  Heart,
  Shield,
  BookOpen,
  AlertTriangle,
  Plus,
  Search,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useAuth } from '../App';

const API = process.env.REACT_APP_BACKEND_URL;

const CompanyDocs = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [missionStatement, setMissionStatement] = useState('');
  const [editingMission, setEditingMission] = useState(false);

  const categories = [
    { value: 'mission', label: 'Mission Statement', icon: Heart, color: 'red' },
    { value: 'msds', label: 'MSDS', icon: AlertTriangle, color: 'orange' },
    { value: 'safety', label: 'Safety Documents', icon: Shield, color: 'blue' },
    { value: 'health', label: 'Health & Safety Manual', icon: BookOpen, color: 'green' },
    { value: 'policy', label: 'Company Policies', icon: FileText, color: 'purple' },
    { value: 'procedure', label: 'Procedures', icon: FileText, color: 'cyan' },
    { value: 'other', label: 'Other', icon: FileText, color: 'gray' }
  ];

  useEffect(() => {
    if (user) {
      fetchDocuments();
      fetchMissionStatement();
    }
  }, [user]);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to view documents');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/api/company-docs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchMissionStatement = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API}/api/company-docs/mission`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMissionStatement(response.data.content || '');
    } catch (error) {
      console.error('Error fetching mission statement:', error);
    }
  };

  const saveMissionStatement = async () => {
    try {
      await axios.post(
        `${API}/api/company-docs/mission`,
        { content: missionStatement },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Mission statement updated');
      setEditingMission(false);
    } catch (error) {
      toast.error('Failed to update mission statement');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await axios.post(`${API}/api/company-docs/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Document uploaded successfully');
      setUploadModalOpen(false);
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await axios.delete(`${API}/api/company-docs/${docId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const response = await axios.get(`${API}/api/company-docs/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const filteredDocs = documents.filter(doc => {
    const matchesSearch = doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.filename?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || doc.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'hr';

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Company Documents</h1>
        <p className="text-slate-600">
          Access all company policies, safety documents, and essential information
        </p>
      </div>

      {/* Mission Statement Section */}
      <Card className="mb-8 bg-gradient-to-br from-red-50 to-pink-50 border-red-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-red-900">
              <Heart className="h-6 w-6 text-red-600" />
              Mission Statement from Jeff
            </CardTitle>
            {isAdmin && (
              <Button
                onClick={() => editingMission ? saveMissionStatement() : setEditingMission(true)}
                variant={editingMission ? "default" : "outline"}
                size="sm"
              >
                {editingMission ? 'Save' : 'Edit'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {editingMission ? (
            <textarea
              value={missionStatement}
              onChange={(e) => setMissionStatement(e.target.value)}
              className="w-full h-48 p-4 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500"
              placeholder="Enter mission statement..."
            />
          ) : (
            <div className="prose max-w-none">
              <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                {missionStatement || 'No mission statement set. Click Edit to add one.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search and Filters */}
      <div className="mb-6 flex gap-4 items-center">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-blue-600" />
          <Input
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-slate-300 text-slate-900"
          />
        </div>
        
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-64 bg-white border-slate-300">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {isAdmin && (
          <Button onClick={() => setUploadModalOpen(true)} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Upload Document
          </Button>
        )}
      </div>

      {/* Documents Grid */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-slate-600">Loading documents...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocs.map(doc => {
            const category = categories.find(c => c.value === doc.category) || categories[categories.length - 1];
            const Icon = category.icon;
            
            return (
              <Card key={doc.id} className="bg-white border-slate-200 hover:shadow-lg transition-shadow">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50 border-b border-slate-200">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className={`h-5 w-5 text-${category.color}-600`} />
                      <CardTitle className="text-lg text-slate-900">{doc.title}</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="text-sm text-slate-600 mb-4">{doc.description || 'No description'}</p>
                  <div className="text-xs text-slate-500 mb-4">
                    <p>Category: {category.label}</p>
                    <p>Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}</p>
                    <p>Size: {(doc.file_size / 1024).toFixed(2)} KB</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => downloadDocument(doc)}
                      size="sm"
                      variant="outline"
                      className="flex-1"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                    {isAdmin && (
                      <Button
                        onClick={() => deleteDocument(doc.id)}
                        size="sm"
                        variant="destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {filteredDocs.length === 0 && !loading && (
        <div className="text-center py-12">
          <FileText className="h-16 w-16 text-slate-700 mx-auto mb-4" />
          <p className="text-slate-600">No documents found</p>
        </div>
      )}

      {/* Upload Modal */}
      {uploadModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Upload Document</h3>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input name="title" required className="bg-white border-slate-300 text-slate-900" />
              </div>
              <div>
                <Label>Description</Label>
                <textarea
                  name="description"
                  className="w-full p-2 border border-slate-300 rounded-lg"
                  rows="3"
                />
              </div>
              <div>
                <Label>Category *</Label>
                <Select name="category" required>
                  <SelectTrigger className="bg-white border-slate-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>File *</Label>
                <Input name="file" type="file" required className="bg-white border-slate-300" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setUploadModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDocs;
